/**
 * AnalyzeGraphCard — Floating draggable card for graph-level analytics.
 *
 * Triggered by [📊 Analyze] button in WorkbenchHeader.
 * Runs: AI Summary · Pattern Scan · Betweenness · PageRank · Communities ·
 * Corridor review · AI-assisted trace.
 * Results overlay graph canvas via useGraphOverlay.
 * Draggable, snaps to TL / BL / BR (TR reserved for Inspector).
 * Keyboard shortcut: G to toggle visibility.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    BarChart3, ScanLine, Search, Activity, TrendingUp, Users,
    GripVertical, X, Loader2, ChevronDown, ChevronRight, Minimize2,
    ShieldAlert, Network
} from 'lucide-react';
import { clsx } from 'clsx';
import { FeatureExplainer } from '../ui/FeatureExplainer';
import { ResultInterpretationCard } from '../ui/ResultInterpretationCard';
import { IntelligenceNoteView } from '../ui/IntelligenceNoteView';
import { usePanelStore } from '../../stores/usePanelStore';
import type { MacroAnalysisType, MacroAiResults } from '../NodeInspector/types';
import { AiProvenanceLine, AiUnavailableNotice, CorridorNoEvidenceNotice, formatTokenAmount } from './SyndicateCorridorCard';

type SnapCorner = 'TL' | 'BL' | 'BR';

interface AnalyzeGraphCardProps {
    /** Override visibility — reads from usePanelStore by default */
    isVisible?: boolean;
    onClose?: () => void;
    /** Called when an analysis is triggered */
    onRunAnalysis?: (type: MacroAnalysisType) => void;
    /** AI-backed results (corridor, AI trace, AI-summary availability) owned by the Workbench. */
    macroAiResults?: MacroAiResults;
    /** When set, the case-scoped rows (corridor review, AI trace) are disabled and this is their tooltip. */
    caseScopedDisabledReason?: string | null;
    /** Override loading — reads from usePanelStore by default */
    loadingAnalysis?: string | null;
    /** Override counts — reads from usePanelStore by default */
    resultCounts?: Record<string, number>;
    className?: string;
    /** Whether the card is embedded inside the Node Inspector (disables floating behavior) */
    isDocked?: boolean;
}

const SNAP_POSITIONS: Record<SnapCorner, { top?: string; bottom?: string; left?: string; right?: string }> = {
    TL: { top: '80px', left: '16px' },
    BL: { bottom: '16px', left: '16px' },
    BR: { bottom: '16px', right: '400px' },  // 380px inspector + gap
};

const ANALYSES: Array<{
    key: MacroAnalysisType;
    label: string;
    icon: typeof ScanLine;
    explainer: { what: string; howItWorks: string; interpret: string };
}> = [
    {
        key: 'ai-summary',
        label: 'AI Summary',
        icon: ScanLine,
        explainer: {
            what: 'Generates a natural-language summary of the current graph, highlighting key patterns and suspicious activity.',
            howItWorks: 'Sends graph structure and metadata to our AI model which produces a structured investigation summary.',
            interpret: 'Use as a starting point for your investigation notes — not as conclusive evidence.',
        },
    },
    {
        key: 'pattern-scan',
        label: 'Pattern Scan',
        icon: Search,
        explainer: {
            what: 'Detects known typologies (peel chains, mixers, fan-out, etc.) in the current graph.',
            howItWorks: 'Runs pattern matching algorithms against known money laundering and fraud typologies.',
            interpret: 'Higher confidence = stronger match. Investigate nodes highlighted in the matched pattern.',
        },
    },
    {
        key: 'betweenness',
        label: 'Betweenness Centrality',
        icon: Activity,
        explainer: {
            what: 'Finds bridge addresses that connect different communities in the graph.',
            howItWorks: 'Runs a graph centrality algorithm — nodes with high betweenness sit on many shortest paths.',
            interpret: 'Higher score = more traffic passes through this address. Investigate the top 3 first.',
        },
    },
    {
        key: 'pagerank',
        label: 'PageRank',
        icon: TrendingUp,
        explainer: {
            what: 'Identifies the most "important" nodes in the graph based on transaction flow.',
            howItWorks: 'Similar to Google\'s PageRank — follows fund flows to find nodes that receive from many important sources.',
            interpret: 'High PageRank = this address is a major recipient of funds. Could be an exchange, a mixer, or a key player.',
        },
    },
    {
        key: 'communities',
        label: 'Community Detection',
        icon: Users,
        explainer: {
            what: 'Groups addresses into clusters based on transaction patterns.',
            howItWorks: 'Uses modularity-based community detection (Louvain/Leiden) to find natural clusters.',
            interpret: 'Same-color nodes transact primarily with each other — they may be controlled by the same entity.',
        },
    },
    {
        key: 'syndicate-corridor',
        label: 'Corridor Review & Playbook',
        icon: ShieldAlert,
        explainer: {
            what: "Looks for a candidate shared upstream funder behind the selected address's feeder wallets, and reports energy delegation, pass-through and possible closed-loop returns as observed facts. Requires an open case.",
            howItWorks: 'Reads the recent transactions of the address and of the feeders shortlisted 2 hops back (and 2 hops forward), groups feeders by a common funding address per token, then asks the configured AI model to draft next steps from those facts only.',
            interpret: 'A candidate funder is a lead, not an attribution: confirm it is not an exchange, bridge or payment service before any notice. When nothing qualifies the review says so without calling the AI model. Any playbook is AI-generated and requires human validation.',
        },
    },
    {
        key: 'ai-trace',
        label: 'AI-Assisted Trace (2 hops each way)',
        icon: Network,
        explainer: {
            what: 'Follows the selected address up to 2 hops backward and 2 hops forward and adds the chosen branches to the canvas. Requires an open case.',
            howItWorks: "At each hop the address's real inbound or outbound transfers are shortlisted and up to 4 branches are picked from that list (by the AI model, or by value when the model is unavailable). Every edge records how it was selected and carries its transaction hash.",
            interpret: "Branches are leads. Check each edge's transaction hash and selection method before relying on it.",
        },
    },
];

const shortAddr = (a: string) => (a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);

/** Corridor review outcome — playbook draft or the honest unavailable state. */
function CorridorResultBlock({ outcome }: { outcome: NonNullable<MacroAiResults['corridor']> }) {
    return (
        <div className="rounded-lg border border-border bg-card px-2.5 py-2 space-y-1.5" data-testid="macro-corridor-result">
            <p className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">
                Corridor review · <span className="font-mono normal-case">{shortAddr(outcome.address)}</span>
            </p>
            <p className="text-[11px] text-muted-foreground" hidden={outcome.status === 'disabled' || outcome.status === 'no_case' || outcome.status === 'error'}>
                {outcome.candidateFunderCount > 0
                    ? `${outcome.candidateFunderCount} candidate shared funder${outcome.candidateFunderCount > 1 ? 's' : ''} (leads; corroboration required before any notice).`
                    : 'No candidate shared funder found.'}
            </p>
            {outcome.status === 'no_evidence' && <CorridorNoEvidenceNotice message={outcome.message} />}
            {(outcome.status === 'unavailable' || outcome.status === 'disabled' || outcome.status === 'no_case') && (
                <AiUnavailableNotice error={outcome.error} />
            )}
            {outcome.status === 'error' && (
                <p className="text-[11px] text-destructive">Corridor review failed: {outcome.error || 'server error'}</p>
            )}
            {outcome.status === 'success' && outcome.playbook && (
                <>
                    <IntelligenceNoteView markdown={outcome.playbook} eyebrow="Playbook draft (AI)" maxHeightClass="max-h-64" />
                    <AiProvenanceLine meta={outcome.aiMeta} />
                </>
            )}
        </div>
    );
}

/** AI-assisted trace outcome — what came back and what was promoted to the canvas. */
function AiTraceResultBlock({ outcome }: { outcome: NonNullable<MacroAiResults['aiTrace']> }) {
    const methods = Object.entries(outcome.selectionMethods || {});
    return (
        <div className="rounded-lg border border-border bg-card px-2.5 py-2 space-y-1.5" data-testid="macro-ai-trace-result">
            <p className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">
                AI-assisted trace · <span className="font-mono normal-case">{shortAddr(outcome.address)}</span>
            </p>
            {(outcome.status === 'disabled' || outcome.status === 'no_case') && (
                <AiUnavailableNotice error={outcome.error || 'the AI-assisted trace is switched off on this server'} />
            )}
            {outcome.status === 'error' && (
                <p className="text-[11px] text-destructive">Trace failed: {outcome.error || 'server error'}</p>
            )}
            {outcome.status === 'ok' && (
                <>
                    <p className="text-[11px] text-foreground">
                        {outcome.nodeCount} address{outcome.nodeCount === 1 ? '' : 'es'} and {outcome.edgeCount} edge{outcome.edgeCount === 1 ? '' : 's'} returned;
                        {' '}{outcome.promotedNodes} new address{outcome.promotedNodes === 1 ? '' : 'es'} and {outcome.promotedEdges} edge{outcome.promotedEdges === 1 ? '' : 's'} added to the canvas.
                    </p>
                    {outcome.summaryText && (
                        <p className="text-[11px] text-muted-foreground">{outcome.summaryText}</p>
                    )}
                    {methods.length > 0 && (
                        <p className="text-[10px] text-muted-foreground">
                            Selected by: {methods.map(([m, c]) => `${m.replace(/_/g, ' ')} (${c})`).join(', ')}
                        </p>
                    )}
                    {outcome.edges.length > 0 && (
                        <ul className="space-y-0.5 max-h-32 overflow-y-auto styled-scrollbar">
                            {outcome.edges.slice(0, 12).map((e, i) => (
                                <li key={`${e.source}-${e.target}-${i}`} className="text-[10px] font-mono text-muted-foreground break-all">
                                    {shortAddr(e.source)} → {shortAddr(e.target)}
                                    {e.value !== null && e.value !== undefined ? ` · ${formatTokenAmount(e.value, e.token_symbol)}` : ''}
                                    {e.tx_hash ? ` · tx ${e.tx_hash.slice(0, 10)}…` : ' · no tx hash'}
                                </li>
                            ))}
                        </ul>
                    )}
                    <AiProvenanceLine meta={outcome.aiMeta} />
                </>
            )}
        </div>
    );
}

function MacroResultsPanel({ macroAi }: { macroAi?: MacroAiResults }) {
    const aiSummary = usePanelStore(s => s.inlineAiSummary);
    const aiLoading = usePanelStore(s => s.inlineAiLoading);
    const patterns = usePanelStore(s => s.inlinePatterns);
    const patternsLoading = usePanelStore(s => s.inlinePatternsLoading);
    const resultCounts = usePanelStore(s => s.analyzeResultCounts);

    const hasAi = Boolean(aiSummary?.trim());
    const aiUnavailable = macroAi?.aiSummaryUnavailable || null;
    const corridor = macroAi?.corridor || null;
    const aiTrace = macroAi?.aiTrace || null;
    const hasPatterns = Array.isArray(patterns) && patterns.length > 0;
    const hasCentrality = (resultCounts.pagerank ?? 0) > 0
        || (resultCounts.betweenness ?? 0) > 0
        || (resultCounts.communities ?? 0) > 0;

    if (!hasAi && !hasPatterns && !hasCentrality && !aiLoading && !patternsLoading && !aiUnavailable && !corridor && !aiTrace) {
        return (
            <div className="mx-1 mt-2 mb-1 rounded-lg border border-dashed border-border/70 bg-muted/20 px-2.5 py-2">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Run AI Summary or Pattern Scan to see findings here. PageRank, Betweenness, and Communities color the canvas when complete.
                </p>
            </div>
        );
    }

    return (
        <div className="mx-1 mt-2 mb-1 space-y-2" data-testid="macro-analysis-results">
            {aiUnavailable && !aiLoading && (
                <AiUnavailableNotice error={aiUnavailable.error} />
            )}

            {(aiLoading || hasAi) && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-2.5">
                    {aiLoading ? (
                        <>
                            <p className="text-[10px] uppercase tracking-wide font-medium text-primary mb-1">
                                Graph summary
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <Loader2 className="w-3 h-3 animate-spin" /> Generating summary…
                            </p>
                        </>
                    ) : (
                        <>
                            <IntelligenceNoteView
                                markdown={aiSummary!}
                                eyebrow="Graph summary"
                                maxHeightClass="max-h-80"
                            />
                            <AiProvenanceLine meta={macroAi?.aiSummaryMeta} className="mt-1.5" />
                        </>
                    )}
                </div>
            )}

            {corridor && <CorridorResultBlock outcome={corridor} />}
            {aiTrace && <AiTraceResultBlock outcome={aiTrace} />}

            {(patternsLoading || hasPatterns) && (
                <div className="rounded-lg border border-border bg-card px-2.5 py-2">
                    <p className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mb-1.5">
                        Pattern findings{hasPatterns ? ` (${patterns!.length})` : ''}
                    </p>
                    {patternsLoading ? (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Loader2 className="w-3 h-3 animate-spin" /> Scanning typologies…
                        </p>
                    ) : (
                        <ul className="space-y-1.5 max-h-40 overflow-y-auto styled-scrollbar">
                            {patterns!.map((p, i) => (
                                <li key={`${p.type}-${i}`} className="text-xs leading-snug">
                                    <span className="font-medium text-foreground">
                                        {p.name || p.type}
                                    </span>
                                    <span className="ml-1.5 text-muted-foreground">
                                        {Math.round((p.confidence ?? 0) * 100)}% confidence
                                    </span>
                                    {p.description && (
                                        <p className="mt-0.5 text-muted-foreground">{p.description}</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {hasCentrality && (
                <div className="rounded-lg border border-border/60 bg-muted/15 px-2.5 py-2">
                    <p className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mb-1">
                        Canvas overlays
                    </p>
                    <ul className="text-[11px] text-muted-foreground space-y-0.5">
                        {(resultCounts.pagerank ?? 0) > 0 && (
                            <li>PageRank scored {resultCounts.pagerank} addresses on the canvas.</li>
                        )}
                        {(resultCounts.betweenness ?? 0) > 0 && (
                            <li>Betweenness scored {resultCounts.betweenness} bridge addresses.</li>
                        )}
                        {(resultCounts.communities ?? 0) > 0 && (
                            <li>Community detection found {resultCounts.communities} clusters (color-coded on canvas).</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

export const AnalyzeGraphCard: React.FC<AnalyzeGraphCardProps> = (props) => {
    // Read from stores with prop override (Phase 0 full)
    const storeVisible = usePanelStore(s => s.showAnalyzeCard);
    const storeLoading = usePanelStore(s => s.analyzeLoading);
    const storeCounts = usePanelStore(s => s.analyzeResultCounts);

    const isVisible = props.isVisible ?? storeVisible;
    const onClose = props.onClose ?? (() => usePanelStore.getState().setShowAnalyzeCard(false));
    const onRunAnalysis = props.onRunAnalysis;
    const loadingAnalysis = props.loadingAnalysis ?? storeLoading;
    const resultCounts = props.resultCounts ?? storeCounts;
    const className = props.className;

    // Original component body below
    const [snap, setSnap] = useState<SnapCorner>('TL');
    const [isMinimized, setIsMinimized] = useState(false);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const cardRef = useRef<HTMLDivElement>(null);

    // Keyboard shortcut: G to close — only for floating (non-docked) mode.
    // Docked cards live in the Analysis tab; Workbench owns G/I globally.
    useEffect(() => {
        if (props.isDocked || !isVisible) return;
        const handler = (e: KeyboardEvent) => {
            // Don't fire if user is typing in an input
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (e.key === 'g' || e.key === 'G') {
                onClose();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [props.isDocked, isVisible, onClose]);

    const cycleSnap = useCallback(() => {
        setSnap(prev => {
            if (prev === 'TL') return 'BL';
            if (prev === 'BL') return 'BR';
            return 'TL';
        });
    }, []);

    if (!isVisible) return null;

    const position = SNAP_POSITIONS[snap];

    return (
        <div
            ref={cardRef}
            className={clsx(
                "transition-all duration-200",
                props.isDocked
                    ? "relative w-full flex flex-col"
                    : "absolute z-20 bg-card/95 backdrop-blur border border-border rounded-xl shadow-overlay " + (isMinimized ? "w-auto" : "w-[280px]"),
                className
            )}
            style={props.isDocked ? undefined : { ...position, position: 'absolute' }}
        >
            {/* Header — always visible */}
            {!props.isDocked && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
                <div className="flex items-center gap-2">
                    <button
                        onClick={cycleSnap}
                        className="cursor-move p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                        title={`Position: ${snap}. Click to move.`}
                    >
                        <GripVertical className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        <BarChart3 className="w-4 h-4 text-primary" />
                        {!isMinimized && <span>Analyze Graph</span>}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setIsMinimized(!isMinimized)}
                        className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                        title={isMinimized ? "Expand" : "Minimize"}
                    >
                        <Minimize2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                        title="Close (G)"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            )}

            {/* Body — docked: natural height (inspector scrolls); floating: bounded. */}
            {(!isMinimized || props.isDocked) && (
                <div className={clsx(
                    'p-2 space-y-0.5',
                    !props.isDocked && 'max-h-[60vh] overflow-y-auto styled-scrollbar',
                )}>
                    {ANALYSES.map(({ key, label, icon: Icon, explainer }) => {
                        const isLoading = loadingAnalysis === key;
                        const hasResults = (resultCounts[key] ?? 0) > 0;
                        const isExpanded = expandedKey === key;
                        const blockedReason = (key === 'ai-trace' || key === 'syndicate-corridor')
                            ? (props.caseScopedDisabledReason || null)
                            : null;

                        return (
                            <div key={key}>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setExpandedKey(isExpanded ? null : key)}
                                        className="p-0.5 text-muted-foreground/60 hover:text-muted-foreground"
                                    >
                                        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    </button>
                                    <button
                                        onClick={() => onRunAnalysis?.(key)}
                                        disabled={isLoading || !!blockedReason}
                                        title={blockedReason || undefined}
                                        data-action={`macro-${key}`}
                                        className={clsx(
                                            "flex-1 flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-all",
                                            isLoading
                                                ? "bg-primary/5 text-primary cursor-wait"
                                                : blockedReason
                                                    ? "text-muted-foreground cursor-not-allowed opacity-60"
                                                    : "hover:bg-accent text-foreground"
                                        )}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <Icon className="w-3.5 h-3.5 shrink-0" />
                                        )}
                                        <span>{label}</span>
                                        {hasResults && (
                                            <span className="ml-auto px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px]">
                                                {resultCounts[key]}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                {isExpanded && (
                                    <div className="ml-5 mt-1 mb-2 space-y-1.5">
                                        <FeatureExplainer
                                            featureKey={`analyze-${key}`}
                                            title={label}
                                            what={explainer.what}
                                            howItWorks={explainer.howItWorks}
                                            interpret={explainer.interpret}
                                        />
                                        {hasResults && (
                                            <ResultInterpretationCard featureKey={`analyze-result-${key}`}>
                                                {explainer.interpret}
                                            </ResultInterpretationCard>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <MacroResultsPanel macroAi={props.macroAiResults} />
                </div>
            )}
        </div>
    );
};
